import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal, ViewChild, effect } from '@angular/core';
import { ChatChannel } from '@app/model/chat-channel';
import { SendbirdService } from '@app/services/sendbird.service';
import { SettingsService } from '@app/services/settings.service';
import { ImageModel, ProfileService } from 'library-explorer';
import { NgScrollbar } from 'ngx-scrollbar';
import { Subject, Subscription } from 'rxjs';
import { delay, filter, map, takeUntil, tap } from 'rxjs/operators';
import { SendBirdChatAdapter } from '../adapters/sendbird-chat.adapter';

@Component({
    selector: 'app-chat-channel-messages-list',
    templateUrl: './chat-channel-messages-list.component.html',
    styleUrls: ['./chat-channel-messages-list.component.scss'],
    providers: [
        SendBirdChatAdapter
    ],
    standalone: false
})
export class ChatChannelMessagesListComponent implements OnInit, OnDestroy {
  @Output() public edit: EventEmitter<SendBird.UserMessage> = new EventEmitter();
  @Output() public markAsRead: EventEmitter<void> = new EventEmitter();
  @Input() public set groupChannel(groupChannel: ChatChannel) {
    this._groupChannel = groupChannel;
    this.initializeChannel();
  }

  public get groupChannel() {
    return this._groupChannel;
  }

  @ViewChild(NgScrollbar, { static: false }) public set scrollbarRef(value: NgScrollbar) {
    if (value && value !== this._scrollbarRef) {
      this._scrollbarRef = value;
      this.scrolbarReady.set(true);
    }
  }

  public get scrollbarRef() {
    return this._scrollbarRef;
  }

  public messages = signal<Array<SendBird.UserMessage | SendBird.FileMessage | SendBird.AdminMessage>>([]);
  public currentUserId: string;
  public canAdministerChannel = false;
  public isLoading = true;
  public globalChatLogo: ImageModel;

  private scrollPosition = 0;
  private messagesQuery: SendBird.PreviousMessageListQuery;
  private readonly unsubscribe: Subject<void> = new Subject();

  public _groupChannel: ChatChannel;
  private _scrollbarRef: NgScrollbar;
  private scrolbarReady = signal(false);

  public loadingIndicator = false;

  constructor(
    private readonly settingsService: SettingsService,
    private readonly profileService: ProfileService,
    private readonly sendbirdService: SendbirdService,
    public readonly chatAdapter: SendBirdChatAdapter
  ) {
    effect(() => {
      this.messages();

      if (!this.scrolbarReady()) {
        return;
      }

      queueMicrotask(() => {
        this.scrollUpdated();
      });
    });
  }

  ngOnInit() {
    this.currentUserId = this.profileService.getCurrentProfileValue()?.id;
    this.canAdministerChannel = this.profileService.isCurrentUserIsAdmin();

    this.addChannelSubscriptions();

    this.settingsService.getSettings()
      .subscribe(data => this.globalChatLogo = data.general.platformLogo);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public deleteMessage(item: SendBird.UserMessage): void {
    this.sendbirdService.deleteMessage(this.groupChannel, item);
  }

  public editMessage(item: SendBird.UserMessage): void {
    this.edit.emit(item);
  }

  public scrollUpdated() {
    if (!this._scrollbarRef) {
      return;
    }
  
    this._scrollbarRef.scrollTo({ bottom: this.scrollPosition, duration: 0 });
  }

  public closeChannel(): void {
    this.sendbirdService.activeChannel.next(null);
    this.sendbirdService.closeChatDialog();
  }

  public scrollToBottom(): void {
    setTimeout(() => {
      this.scrollbarRef.scrollTo({ bottom: 0, duration: 0 });
      this.scrollbarRef.update();
    }, 50);
  }

  public onScroll(event: Event): void {
    if (this.isLoading || !this.messagesQuery?.hasMore) {
      return;
    }

    const el = event.target as HTMLElement;

    const scrollTop = el.scrollTop;
    const distanceToBottom = el.scrollHeight - el.offsetHeight - scrollTop;

    this.scrollPosition = distanceToBottom;

    if (scrollTop < 100) {
      this.loadMessages();
    }
  }

  private addChannelSubscriptions(): void {
    this.sendbirdService.loadingIndicatorId
      .subscribe(url => this.loadingIndicator = url && url === this.groupChannel?.url);

    this.sendbirdService.messageReceived
      .pipe(
        takeUntil(this.unsubscribe),
        filter(data => data.channelUrl === this.groupChannel?.url)
      )
      .subscribe(data => {
        this.messages.update(list => [...list, data.message]);

        this.markAsRead.emit();
      });

    this.sendbirdService.messageDeleted
      .pipe(
        takeUntil(this.unsubscribe),
        filter(data => data.channelUrl === this.groupChannel.url),
        map(data => this.messages().findIndex(message => message.messageId === data.messageId)),
        filter(index => index !== -1)
      )
      .subscribe(index => {
        this.messages.update(list => {
          const copy = [...list];
          copy.splice(index, 1);
          return copy;
        });
      });

    this.sendbirdService.messageUpdated
      .pipe(
        takeUntil(this.unsubscribe),
        filter(data => data.channelUrl === this.groupChannel.url)
      )
      .subscribe(data => {
        const index = this.messages().findIndex(message => message.messageId === data.message.messageId);

        if (index !== -1) {
          this.messages.update(list => {
            const copy = [...list];
            copy.splice(index, 1, data.message);
            return copy;
          });
        }
      });

    this.sendbirdService.messageSent
      .pipe(
        takeUntil(this.unsubscribe),
        delay(200)
      ).subscribe(() => {
        this.scrollbarRef.scrollTo({ bottom: 0 })
      });
  }

  private initializeChannel(): void {
    this.messages.set([]);
    this.scrollPosition = 0;

    this.markAsRead.emit();
    this.messagesQuery = this.sendbirdService.getPreviousMessagesQuery(this.groupChannel);

    this.loadMessages();
  }

  private loadMessages(): void {
    if (this.messagesQuery.hasMore) {
      this.isLoading = true;

      this.messagesQuery.load(data => {
        if (data && data.length) {
          this.messages.update(list => [...data, ...list]);
        }

        this.isLoading = false;
      });
    }
  }
}
